/**
 * Wire any feed to the alert engine.
 *
 * A view passes its records and its source definition; the hook evaluates every enabled rule for
 * that source whenever the records change, and delivers only for (rule, record) pairs that have not
 * already fired. Views stay unaware of storage, throttling and delivery.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertFieldDef, AlertRule, AlertSourceDef, explainMatch, ruleMatches } from '../utils/alertRules';
import {
  deliver,
  hasFired,
  loadRules,
  markFired,
  noteFired,
  saveRules,
  withinThrottle,
} from '../utils/alertRuntime';

/** Rules live in one store; every view sees edits made anywhere. */
const RULES_EVENT = 'mmec-alert-rules-changed';

export function publishRules(rules: AlertRule[]): void {
  saveRules(rules);
  try {
    window.dispatchEvent(new CustomEvent(RULES_EVENT, { detail: rules }));
  } catch {
    /* ignore */
  }
}

export function useAlertRuleStore(): [AlertRule[], (next: AlertRule[]) => void] {
  const [rules, setRules] = useState<AlertRule[]>(() => loadRules());

  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setRules(Array.isArray(detail) ? detail : loadRules());
    };
    window.addEventListener(RULES_EVENT, onChange);
    return () => window.removeEventListener(RULES_EVENT, onChange);
  }, []);

  const update = useCallback((next: AlertRule[]) => {
    setRules(next);
    publishRules(next);
  }, []);

  return [rules, update];
}

export interface UseAlertRulesOptions {
  /** Skip evaluation entirely (e.g. the feed is stale or the view is hidden). */
  paused?: boolean;
}

/**
 * Evaluate this source's rules against `records`. Returns the number of matches delivered on the
 * most recent pass, which the panel uses to show that a rule is live.
 */
export function useAlertRules(
  source: AlertSourceDef,
  records: Record<string, unknown>[],
  options: UseAlertRulesOptions = {},
): { rules: AlertRule[]; firedCount: number } {
  const [rules] = useAlertRuleStore();
  const [firedCount, setFiredCount] = useState(0);
  const paused = options.paused === true;

  const fieldMap = useMemo(
    () =>
      source.fields.reduce(
        (acc, f) => {
          acc[f.key] = f;
          return acc;
        },
        {} as Record<string, AlertFieldDef>,
      ),
    [source.fields],
  );

  const active = useMemo(
    () => rules.filter((r) => r.enabled && r.sourceId === source.id),
    [rules, source.id],
  );

  useEffect(() => {
    if (paused || !active.length || !records.length) return;
    let delivered = 0;
    for (const rule of active) {
      if (withinThrottle(rule)) continue;
      for (const record of records) {
        if (!ruleMatches(record, rule, fieldMap)) continue;
        const key = source.recordKey(record);
        if (hasFired(rule.id, key)) continue;
        markFired(rule.id, key);
        noteFired(rule);
        deliver(rule, {
          id: `${rule.id}|${key}|${Date.now()}`,
          ruleId: rule.id,
          ruleName: rule.name,
          sourceId: source.id,
          sourceLabel: source.label,
          recordKey: key,
          summary: source.describe(record),
          // Why it fired, in the record's own numbers.
          details: explainMatch(record, rule.root, fieldMap),
          historical: source.historicalParams?.(record) ?? undefined,
          at: Date.now(),
        });
        delivered += 1;
        // One delivery per rule per pass keeps a burst of matching rows from becoming a burst of
        // popups; the rest are picked up on later passes once the throttle clears.
        break;
      }
    }
    if (delivered) setFiredCount((n) => n + delivered);
  }, [records, active, fieldMap, source, paused]);

  return { rules: active, firedCount };
}
